import { Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext'
import Layout from './components/Layout/Layout'
import LoginPage from './pages/auth/LoginPage'
import Dashboard from './pages/Dashboard'
import GymList from './pages/gyms/GymList'
import GymDetail from './pages/gyms/GymDetail'
import CreateGym from './pages/gyms/CreateGym'
import EditGym from './pages/gyms/EditGym'
import KanbanBoard from './pages/kanban/KanbanBoard'
import ChecklistPage from './pages/checklists/ChecklistPage'
import ChecklistReview from './pages/checklists/ChecklistReview'
import PreWorkChecklist from './pages/checklists/PreWorkChecklist'
import PreWorkChecklistPage from './pages/pre-work-checklist/PreWorkChecklistPage'
import GymRekiDetail from './pages/checklists/GymRekiDetail'
import ApprovalsPage from './pages/approvals/ApprovalsPage'
import ApprovalLogs from './pages/approvals/ApprovalLogs'
import HOAApprovalPage from './pages/approvals/HOAApprovalPage'
import UsersPage from './pages/users/UsersPage'
import ProfilePage from './pages/profile/ProfilePage'
import LoadingSpinner from './components/common/LoadingSpinner'
import COODashboard from './pages/coo/COODashboard';
import CEODashboard from './pages/ceo/CEODashboard';
import EscalationReports from './pages/coo/EscalationReports';
import ReportsTab from './components/coo/ReportsTab';
import ExpiredClients from './pages/expired-clients/ExpiredClients';
import PWAInstallPrompt from './components/common/PWAInstallPrompt';
import ReloadPrompt from './components/common/ReloadPrompt';

// Protected Route Component
const ProtectedRoute = ({ children, requiredRole = null, restrictedForHeadOfAcquisition = false, facilityTeamOnly = false }) => {
  const { user, loading } = useAuth()
  const location = useLocation()
  
  console.log('🔒 ProtectedRoute check:', {
    loading,
    user: user ? { id: user.id, role: user.role.name, level: user.role.level } : null,
    facilityTeamOnly,
    requiredRole,
    restrictedForHeadOfAcquisition,
    currentPath: location.pathname
  });
  
  if (loading) {
    console.log('⏳ ProtectedRoute: showing loading...');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }
  
  if (!user) {
    console.log('❌ ProtectedRoute: no user, redirecting to login');
    return <Navigate to="/login" replace />
  }

  // Facility Team restrictions - only access to My Tasks
  if (facilityTeamOnly && user.role.level !== 5) {
    console.log('❌ ProtectedRoute: facilityTeamOnly but user level is not 5, redirecting to dashboard');
    return <Navigate to="/dashboard" replace />
  }

  // Block facility team from accessing other pages
  if (user.role.level === 5 && !facilityTeamOnly &&
      !location.pathname.includes('/dashboard')) {
    console.log('❌ ProtectedRoute: facility team trying to access restricted page, redirecting to dashboard');
    return <Navigate to="/dashboard" replace />
  }

  // Specific role requirements - FIXED: Lower numbers = higher privileges
  if (requiredRole && user.role.level > requiredRole) {
    console.log('❌ ProtectedRoute: insufficient role level, redirecting to dashboard');
    console.log('🔍 Role check details:', { userLevel: user.role.level, requiredLevel: requiredRole, comparison: user.role.level > requiredRole });
    return <Navigate to="/dashboard" replace />
  }

  // Head of Acquisition restrictions
  if (restrictedForHeadOfAcquisition && user.role.level === 3) {
    console.log('❌ ProtectedRoute: restricted for Head of Acquisition, redirecting to dashboard');
    return <Navigate to="/dashboard" replace />
  }

  console.log('✅ ProtectedRoute: Access granted for path:', location.pathname, 'User role:', user?.role?.name, 'Level:', user?.role?.level)
  return children
}

// COO Only Route Component
const COOOnlyRoute = ({ children }) => {
  const { user, loading } = useAuth()
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }
  
  if (!user || user.role.level !== 2) {
    return <Navigate to="/dashboard" replace />
  }
  
  return children
}

// CEO Only Route Component
const CEOOnlyRoute = ({ children }) => {
  const { user, loading } = useAuth()
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }
  
  if (!user || user.role.level !== 1) {
    return <Navigate to="/dashboard" replace />
  }
  
  return children
}

// Dashboard Redirect Component
const DashboardRedirect = () => {
  const { user } = useAuth()
  
  // Redirect CEO to their specific dashboard
  if (user?.role?.level === 1) {
    return <Navigate to="/ceo-dashboard" replace />
  }
  
  // Redirect COO to their specific dashboard
  if (user?.role?.level === 2) {
    return <Navigate to="/coo-dashboard" replace />
  }
  
  // All other users go to Dashboard
  return <Dashboard />
}

function App() {
  const { user, loading } = useAuth()

  console.log('🔍 App component - User:', user);
  console.log('🔍 App component - Loading:', loading);

  if (loading) {
    console.log('⏳ App: Still loading auth...');
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-gray-600">Loading WTF Gym Acquisition...</p>
        </div>
      </div>
    )
  }

  console.log('✅ App: Auth loaded, user role level:', user?.role?.level);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* PWA Components */}
      <PWAInstallPrompt />
      <ReloadPrompt />

      <Routes>
        {/* Public Routes */}
        <Route 
          path="/login" 
          element={
            user ? (
              // Redirect based on role after login
              (() => {
                console.log('🔄 Login redirect logic - User:', user.role.name, 'Level:', user.role.level);
                if (user.role.level === 5) {
                  console.log('➡️ Redirecting facility team to /dashboard');
                  return <Navigate to="/dashboard" replace />;
                } else {
                  console.log('➡️ Redirecting to /dashboard');
                  return <Navigate to="/dashboard" replace />;
                }
              })()
            ) : (
              <LoginPage />
            )
          } 
        />
        
        {/* Protected Routes */}
        <Route 
          path="/" 
          element={
            <ProtectedRoute>
              <Layout />
            </ProtectedRoute>
          }
        >
          {/* Dashboard - redirects to role-specific dashboards */}
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardRedirect />} />

          {/* Role-specific Dashboards */}
          <Route 
            path="coo-dashboard" 
            element={
              <COOOnlyRoute>
                <COODashboard />
              </COOOnlyRoute>
            } 
          />

          <Route 
            path="ceo-dashboard" 
            element={
              <CEOOnlyRoute>
                <CEODashboard />
              </CEOOnlyRoute>
            } 
          />

          {/* COO Specific Routes */}
          <Route 
            path="coo/escalations" 
            element={
              <COOOnlyRoute>
                <Layout>
                  <EscalationReports />
                </Layout>
              </COOOnlyRoute>
            } 
          />
          <Route 
            path="coo/reports" 
            element={
              <COOOnlyRoute>
                <Layout>
                  <ReportsTab />
                </Layout>
              </COOOnlyRoute>
            } 
          />

          {/* Regular Protected Routes (blocked for facility team) */}
          <Route 
            path="gyms" 
            element={
              <ProtectedRoute>
                <GymList />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="gyms/create" 
            element={
              <ProtectedRoute requiredRole={4}>
                <CreateGym />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="gyms/:id" 
            element={
              <ProtectedRoute>
                <GymDetail />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="gyms/:id/edit" 
            element={
              <ProtectedRoute requiredRole={4}>
                <EditGym />
              </ProtectedRoute>
            } 
          />
          <Route 
            path="kanban" 
            element={
              <ProtectedRoute>
                <KanbanBoard />
              </ProtectedRoute>
            } 
          />
          
          {/* Checklist Routes */}
          <Route 
            path="checklists" 
            element={
              <ProtectedRoute>
                {console.log('🔍 Route matched: /checklists')}
                <ChecklistPage />
              </ProtectedRoute>
            } 
          />
          <Route
            path="checklists/review"
            element={
              <ProtectedRoute requiredRole={3}>
                <ChecklistReview />
              </ProtectedRoute>
            }
          />
          {/* Pre-Work Checklist with gym context */}
          <Route
            path="pre-work-checklist/:gymId"
            element={
              <ProtectedRoute>
                <PreWorkChecklistPage />
              </ProtectedRoute>
            }
          />
          
          {/* Approval Routes */}
          <Route
            path="approvals"
            element={
              <ProtectedRoute requiredRole={3}>
                <ApprovalsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="approvals/logs"
            element={
              <ProtectedRoute requiredRole={3}>
                <ApprovalLogs />
              </ProtectedRoute>
            }
          />
          <Route
            path="hoa-approvals"
            element={
              <ProtectedRoute requiredRole={3}>
                <HOAApprovalPage />
              </ProtectedRoute>
            }
          />
          


          {/* User Management */}
          <Route
            path="users"
            element={
              <ProtectedRoute requiredRole={3}>
                <UsersPage />
              </ProtectedRoute>
            }
          />
          
          {/* Expired Clients - CEO and HOA only */}
          <Route
            path="expired-clients"
            element={
              <ProtectedRoute requiredRole={3}>
                <ExpiredClients />
              </ProtectedRoute>
            }
          />
          
          {/* Profile */}
          <Route 
            path="profile" 
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            } 
          />
        </Route>

        {/* Test route for debugging */}
        <Route 
          path="/test-ceo" 
          element={
            <div className="p-6">
              <h1 className="text-2xl font-bold mb-4">CEO Test Page</h1>
              <pre className="bg-gray-100 p-4 rounded">
                {JSON.stringify(user, null, 2)}
              </pre>
            </div>
          } 
        />

        {/* Catch all route */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </div>
  )
}

export default App 